import StringUtils from "src/utils/stringUtils";

export function generatePieChartData(items: Map<string, number> | undefined, totalCli: number) {
  if (totalCli === 0) {
    return [];
  } else {
    if (items) {
      // Convert the map entries to an array and iterate over them
      return Object.entries(items).map(([label, value]) => ({
        title: convertCamelCaseToTitleCase(label),
        percentage: calculatePercentage(value, totalCli),
        value: value,
      }));
    } else {
      return [];
    }
  }

}

function calculatePercentage(value: number | undefined, total: number): number {
  if (value !== undefined) {
    return (value / total) * 100;
  }
  return 0;
}

export function convertCamelCaseToTitleCase(input: string): string {
  switch (input) {
    case 'failed':
    case 'success':
    case 'ZeroVariance':
      return StringUtils.convertCamelCaseToTitleCase(input);
    case 'Apttus data Missing':
      return 'Apttus Data Missing';
    default:
      return input;
  }
}

export const CREATE_TICKET_URL = 'https://t.corp.amazon.com/create';

const REASON_CODE_DESCRIPTIONS: Record<string, string> = {
  // Coarse infrastructure reason codes (ReportGenerationReasonCode enum) - used only when the
  // variance analyser produced no granular root cause for the CLI.
  'REPORT_GENERATION_ERROR': 'An unexpected error occurred while generating the report for this {unit}.',
  'GL_SERVICE_FAILURE': 'Unable to pull balances from the GL service for this {unit}.',
  'SQS_PUBLISH_FAILURE': 'Unable to start report generation for this {unit}.',
  'TP_CHARGE_DATA_NOT_FOUND': 'TP charge data was not found in ATP for this {unit}. Verify that ATP has calculated and posted charges for this period.',
  'APTTUS_DATA_MISSING': 'Required Apttus data is missing for this {unit}. Verify the {unit} configuration in Apttus.',
  'TIMEOUT': 'Report generation timed out for this {unit}.',
  // Granular variance-analyser root causes (CLIVarianceRootCauseTypes) surfaced from the variance record.
  'PLI Config Missing': 'PLI Step 6 is not configured for this CLI. This is a business configuration issue.',
  'Apttus data Missing': 'Required Apttus data is missing for this CLI. Verify the CLI configuration in Apttus.',
  'GL Posting Failed': "The CLI's journal posting to the GL failed in ATP, so no TP charge was booked and the report could not be produced.",
  'Invalid data source': 'The datasource for this CLI is invalid, so report generation could not proceed.',
  'TP markup rate missing in Apttus': 'No TP policy rate is configured or flowing from Apttus into ATP for this CLI.',
  'TP markup rate not used in step-3': 'Apttus provides a TP rate, but the Step 3 Calc Builder does not use it, so no calculated rate is produced.',
  'Incorrect PLI Configuration Issue': 'PLI Step 6 is configured incorrectly for this CLI. This is a business configuration issue.',
  'PLI Mismatch': 'The PLI type in Apttus does not match the PLI configured in the calculation (Step 6).',
  'ATP didn\'t run for this CLI': 'The CLI is terminated or inactive, so no transfer pricing calculation runs for this period.',
};

// Coarse infrastructure reason codes (ReportGenerationReasonCode enum). Each maps to its own
// human-readable label rather than a single generic "Technical Issue" bucket.
const REASON_CODE_LABELS: Record<string, string> = {
  'REPORT_GENERATION_ERROR': 'Report Generation Error',
  'GL_SERVICE_FAILURE': 'GL Service Failure',
  'SQS_PUBLISH_FAILURE': 'SQS Publish Failure',
  'TP_CHARGE_DATA_NOT_FOUND': 'TP Charge Data Not Found',
  'TIMEOUT': 'Timeout',
  'UNKNOWN': 'Unknown',
};


export function getReasonCodeDescription(reasonCode: string, unitNoun = 'CLI'): string {
  const template = REASON_CODE_DESCRIPTIONS[reasonCode] || reasonCode;
  // Coarse (report-type-agnostic) descriptions use a {unit} placeholder so the tooltip reads with the
  // report's own unit noun — CLI for MAR, entity for LEF, workbook for CWB. Granular MAR-only root-cause
  // descriptions have no placeholder and are unaffected.
  return template.replace(/\{unit\}/g, unitNoun);
}

export function getDisplayReasonCode(reasonCode: string): string {
  if (!reasonCode) return reasonCode;
  // Coarse enum codes -> a distinct friendly label.
  if (REASON_CODE_LABELS[reasonCode]) return REASON_CODE_LABELS[reasonCode];
  // Granular variance-analyser root causes (e.g. "GL Posting Failed") and the PLI/Apttus labels
  // are already human-readable, so display them as-is.
  return reasonCode;
}

